// Phase 6 Step 1 — screen-as-data: the pure evaluator + tree builder (no DOM, no globals).
//
// `evaluateScreen(screen, row)` walks a filter tree and returns whether the row passes. `buildScreenTree`
// turns today's flat filter object (getScreenerFilters output) into a flat-AND tree of every registered
// clause — emitting ALL clauses (each internally no-ops on a neutral value, exactly like the old guards)
// so Step-1 equivalence never depends on omission. AND/OR/NOT are supported for future compound screens.

import { group, leaf, screen, OPS } from './schema.js';
import { CLAUSES, CLAUSE_BY_ID } from './registry.js';

/** Evaluate one node (group or leaf) against a row. */
function evalNode(node, row) {
    if (!node) return true;
    if (node.t === 'f') {
        const c = CLAUSE_BY_ID[node.id];
        return c ? c.pass(row, node.p) : true;   // unknown clause = no constraint (graceful)
    }
    // group
    const kids = node.kids || [];
    if (node.op === OPS.OR) return kids.some(k => evalNode(k, row));
    if (node.op === OPS.NOT) return !kids.every(k => evalNode(k, row));   // NOT over the AND of kids
    return kids.every(k => evalNode(k, row));   // AND (default)
}

/** Does `row` pass the `screen` (a versioned envelope {v,root} or a bare node)? */
export function evaluateScreen(screenOrNode, row) {
    const root = (screenOrNode && screenOrNode.root !== undefined) ? screenOrNode.root : screenOrNode;
    return evalNode(root, row);
}

/**
 * Build a screen (flat AND of every clause) from the flat filter object produced by
 * getScreenerFilters(). Emits all clauses; each captures its params by value → serializable.
 */
export function buildScreenTree(filters) {
    return screen(group(OPS.AND, CLAUSES.map(c => leaf(c.id, c.build(filters)))));
}
